import { defineStore } from 'pinia';

export enum Hotkeys {
  AltT = 'alt+t',
  AltA = 'alt+a',
  AltS = 'alt+s',
  AltV = 'alt+v',
  Alt1 = 'alt+1',
  Alt2 = 'alt+2',
  Alt3 = 'alt+3',
  Alt4 = 'alt+4',
  Alt5 = 'alt+5',
  Alt6 = 'alt+6',
  Alt7 = 'alt+7',
  Alt8 = 'alt+8',
  Alt9 = 'alt+9',
  Escape = 'escape'
}

type HotkeyHandler = () => void;

export const useHotkeyManager = defineStore('hotkeyManager', () => {
  const { t } = useEkI18n();

  const pageHotkeys = ref<Record<string, HotkeyHandler>>({});
  const navbarHotKeys = ref<Record<string, HotkeyHandler>>({});
  const modalHotkeysMap = ref(new Map<HTMLElement, Record<string, HotkeyHandler>>());

  function registerPageHotkeys(hotkeys: Record<string, HotkeyHandler>) {
    clearPageHotkeys();
    pageHotkeys.value = hotkeys;
  }

  function registerModalHotkeys(hotkeys: Record<string, HotkeyHandler>, modalElement: HTMLElement) {
    if (!modalElement) {
      return;
    }

    modalHotkeysMap.value.set(modalElement, hotkeys);
  }

  function registerNavbarHotkeys(hotkeys: Record<string, HotkeyHandler>) {
    navbarHotKeys.value = hotkeys;
  }

  function clearModalHotkeys(modalElement: HTMLElement) {
    modalHotkeysMap.value.delete(modalElement);
  }

  function clearPageHotkeys() {
    pageHotkeys.value = {};
  }

  function clearNavbarHotkeys() {
    navbarHotKeys.value = {};
  }

  function generateNavbarHotkeys(
    navButtons: { path: string }[],
    navigateTo: (path: string) => void
  ) {
    const hotkeyMap: Record<string, () => void> = {};

    navButtons.forEach((button, index) => {
      if (index < 9) {
        hotkeyMap[`alt+${index + 1}`] = () => navigateTo(button.path);
      }
    });

    registerNavbarHotkeys(hotkeyMap);
  }

  async function getHandler(keyCombo: string, allowPageAndNavbar = true): Promise<HotkeyHandler | undefined> {
    const topModal = await modalController.getTop();

    if (topModal && modalHotkeysMap.value.has(topModal)) {
      const hotkeys = modalHotkeysMap.value.get(topModal)!;

      if (hotkeys[keyCombo]) return hotkeys[keyCombo];

      return; // prevent page/navbar hotkeys if top modal has hotkeys registered but combo not found
    }

    if (!allowPageAndNavbar) return undefined;

    if (topModal) return; // modal open but no hotkeys for it

    return pageHotkeys.value[keyCombo] || navbarHotKeys.value[keyCombo];
  }

  async function handleKeydown(event: KeyboardEvent) {
    if (!event.altKey && event.key && event.key !== 'Escape') return;

    if (isAlertOpen()) {
      return; 
    }

    const key = event.key?.toLowerCase();
    const combo = event.altKey ? `alt+${key}` : key;
    const modalIsOpen = Object.keys(modalHotkeysMap.value).length > 0;
    const handler = await getHandler(combo, !modalIsOpen);

    if (handler) {
      event.preventDefault();
      handler();
    }
  }

  function isAlertOpen() {
    return !!document.querySelector('ion-alert') || !!document.querySelector('ion-loading');
  }

  interface Hotkey {
    combo: string;
    description: string;
    category: string;
  }

  const generalHotkeys = computed<Hotkey[]>(() => {
    return [
      { combo: Hotkeys.AltT, description: t('hotkeys.createTimeRegistration'), category: 'general' },
      { combo: Hotkeys.AltA, description: t('hotkeys.createAppointment'), category: 'general' },
      { combo: Hotkeys.AltS, description: t('hotkeys.createCase'), category: 'general' },
      { combo: Hotkeys.AltV, description: t('hotkeys.createItem'), category: 'general' },
      {
        combo: 'alt+1-9',
        description: t('hotkeys.switchView'),
        category: 'general'
      },

      { combo: Hotkeys.AltS, description: t('hotkeys.save'), category: 'dialog' },
      { combo: Hotkeys.Escape, description: t('hotkeys.close'), category: 'dialog' },
      { combo: Hotkeys.AltA, description: t('hotkeys.saveAndCreateNew'), category: 'dialog' }
    ];
  });

  const groupedHotkeys = computed(() => {
    return generalHotkeys.value.reduce((acc, hotkey) => {
      if (!acc[hotkey.category]) {
        acc[hotkey.category] = [];
      }

      acc[hotkey.category].push(hotkey);

      return acc;
    }, {} as { [category: string]: Hotkey[] });
  });

  return {
    handleKeydown,
    registerPageHotkeys,
    registerModalHotkeys,
    clearModalHotkeys,
    clearPageHotkeys,
    registerNavbarHotkeys,
    clearNavbarHotkeys,
    generateNavbarHotkeys,
    isAlertOpen,

    _modalHotkeysMap: modalHotkeysMap,
    _pageHotkeys: pageHotkeys,
    _navbarHotKeys: navbarHotKeys,
    modalHotkeysMap: readonly(modalHotkeysMap),
    pageHotkeys: readonly(pageHotkeys),
    navbarHotKeys: readonly(navbarHotKeys),

    generalHotkeys,
    groupedHotkeys
  };
});